import { Button, Paper, Text } from "@mantine/core";
import type { UseFormReturnType } from "@mantine/form";
import IconLink from "@src/assets/icons/tabler/link.svg?react";
import { NativeLikeTooltip as Tooltip } from "@src/components/ui/native-like-tooltip";
import clsx from "clsx";
import type { ReactNode } from "react";
import type { z } from "zod";
import { EmailInputSection } from "./email-input-section";
import { ShareInviteUser } from "./share-whiteboard-user-form-field";
import type { inviteFormSchema } from "./whiteboard-invite-schema";

type InviteFormLayoutProps = {
	form: UseFormReturnType<z.infer<typeof inviteFormSchema>>;
	handleSubmit: (event?: React.FormEvent<HTMLFormElement>) => void;
	entityName: string;
	copyLinkLabel: string;
	isCopyLinkTooltipOpen: boolean;
	onCopyLink: () => void;
	emailControlsEnabled?: boolean;
	emailControlsStats?: { available: boolean };
	disableEditorRoles?: boolean;
	footerNotice?: ReactNode;
};

export function InviteFormLayout({
	form,
	handleSubmit,
	entityName,
	copyLinkLabel,
	isCopyLinkTooltipOpen,
	onCopyLink,
	emailControlsEnabled = true,
	emailControlsStats,
	disableEditorRoles = false,
	footerNotice,
}: InviteFormLayoutProps) {
	return (
		<form onSubmit={handleSubmit} className="flex flex-col gap-4">
			<Paper
				withBorder
				p="md"
				radius="md"
				className="bg-surface-subtle shadow-sm border-border-subtle transition-all duration-300 hover:shadow-md"
			>
				<div className="flex flex-col gap-2">
					<Text size="md" fw={700} className="text-text-primary" mb={4}>
						Invite Members
					</Text>
					<EmailInputSection
						form={form}
						handleSubmit={handleSubmit}
						isSubmitting={form.submitting}
						enabled={emailControlsEnabled}
						stats={emailControlsStats}
					/>
				</div>
			</Paper>

			{footerNotice}

			{form.values.users.length > 0 && (
				<div className="flex flex-col gap-2">
					<Text
						size="xs"
						fw={700}
						c="dimmed"
						className="uppercase tracking-widest ml-1"
					>
						Collaborators
					</Text>
					<Paper
						withBorder
						radius="md"
						className={clsx(
							"overflow-hidden bg-surface-subtle border-border-subtle",
							"shadow-sm transition-all duration-300 hover:shadow-md",
						)}
					>
						<div className="flex flex-col">
							{form.values.users.map((user, index) => (
								<div
									key={user.email}
									className={clsx(
										"p-3 transition-colors hover:bg-surface-hover-subtle",
										index !== form.values.users.length - 1 &&
											"border-b border-border-subtle",
									)}
								>
									<ShareInviteUser
										form={form}
										index={index}
										user={user}
										entityName={entityName}
										handleSubmit={handleSubmit}
										disableEditor={disableEditorRoles}
									/>
								</div>
							))}
						</div>
					</Paper>
				</div>
			)}

			<div className="pt-2">
				<Tooltip
					zIndex={9999}
					label={`${copyLinkLabel} Copied`}
					opened={isCopyLinkTooltipOpen}
					withArrow
					openDelay={500}
				>
					<Button
						fullWidth
						variant="filled"
						color="var(--ui-color-brand-primary)"
						size="sm"
						radius="sm"
						leftSection={<IconLink className="w-4 h-4" />}
						onClick={onCopyLink}
						className="shadow-sm hover:shadow-md transition-all duration-200 hover:scale-[1.01] active:scale-[0.99] cursor-pointer"
					>
						{`Copy ${copyLinkLabel}`}
					</Button>
				</Tooltip>
			</div>
		</form>
	);
}
